import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _sortBy from 'lodash/sortBy';
import _orderBy from 'lodash/orderBy';
import _isEmpty from 'lodash/isEmpty';
import _intersection from 'lodash/intersection';
import { add, format, nextSaturday, subDays, subWeeks } from 'date-fns';
import { alphabet, formatNoteDetailsDate } from 'utils';
import { getEachMinuteOfInterval, getCurrentWeekDayISO, getCurrentLocalTimeHHMM } from 'utils/dateUtils';
import {
  PRICE_ADJUST,
  GIFT_WRAP_LINE_ITEM,
  LOYALTY_CARD_TYPES,
  NO_IMAGE_AVAILABLE,
  CLIENT_WISHLIST_STORAGE_NAME,
} from 'constants/index';
import { ORDER_SERVICES_DEPT_TYPE } from 'constants/orders';
import { missingCardMessage } from 'modules/clients/constants';
import { convertStylePreferences } from './stylePreferenceConverter';

export * from './stylePreferenceConverter';

const requiredKeys = ['firstName', 'lastName', 'line1', 'city', 'country', 'state', 'zip', 'phone'];
/**
 * Function to validate all required fields on billingAddress
 * @param {{ [x: string]: string }} billingAddress
 */
export const validateBillingAddress = (billingAddress) => {
  const uncheckedKeys = requiredKeys.reduce((arr, key) => {
    if (!billingAddress[key]) {
      arr.push(key);
    }

    return arr;
  }, []);

  return uncheckedKeys.length === 0;
};

/**
 * Function to validate payment card
 * @param {{ payments: *[] }} param0
 */
export const validatePaymentCard = ({ payments }, selectedCardBillingAddress) => {
  const payment = payments.find((p) => p.selected) || payments.find((p) => p.defaultCard) || {};
  const billingAddress = payment.billingAddress || selectedCardBillingAddress;

  if (!_isEmpty(billingAddress)) {
    return validateBillingAddress(billingAddress);
  }

  return false;
};

/**
 * Function to get intersection between kiNumbersForInCircle and paymentMethods
 * @param {Array<string>} kiNumbersForInCircle
 * @param {Array<{ keyIdentifier: string }>} paymentMethods
 * @returns {Array<string>}
 */
export const getKiNumbers = (kiNumbersForInCircle, paymentMethods = []) => {
  // It seems below fallback case is outdated need to double checked if we want to remove duplicated calls to loyalty endpoint
  // https://neimanmarcus.atlassian.net/browse/OSP-4070
  return kiNumbersForInCircle
    ? paymentMethods.reduce((acc, { keyIdentifier }) => {
        if (kiNumbersForInCircle.includes(keyIdentifier)) {
          return acc.concat(keyIdentifier);
        }
        return acc;
      }, [])
    : // fallback to paymentMethods only
      paymentMethods.reduce((acc, { keyIdentifier }) => {
        if (keyIdentifier) {
          return acc.concat(keyIdentifier);
        }
        return acc;
      }, []);
};

/**
 * Function returns error message for order submit request
 * @param {Object} err
 * @returns {string} errorMessage
 */
export const extractSubmitErrorMessage = (err) => {
  let errorMessage = _get(err, 'response.data.message', 'Something went wrong');
  if (errorMessage.match(/instance.soldTo.address requires property/g)?.length > 1) {
    errorMessage = missingCardMessage;
  }
  return errorMessage;
};

const convertFilterValueToSeconds = (value) => {
  switch (value) {
    case 'three-days':
      return format(subDays(Date.now(), 3), 't');

    case 'week':
      return format(subWeeks(Date.now(), 1), 't');

    case 'month':
    case 'more-than-a-month':
      return format(subDays(Date.now(), 30), 't');

    case 'quarter':
      return format(subDays(Date.now(), 90), 't');

    default:
      return '';
  }
};

export const getAdjustedPrice = (productPrice, adjustment, priceType) => {
  if (!productPrice || !adjustment) {
    return null;
  }

  let adjustedPrice;
  const { DOLLAR, PERCENT, OVERRIDE, ADJUSTMENT } = PRICE_ADJUST.TYPES_CHARS;
  switch (priceType) {
    case DOLLAR:
      adjustedPrice = productPrice - adjustment;
      break;
    case PERCENT:
      adjustedPrice = productPrice - (productPrice * adjustment) / 100;
      break;
    case OVERRIDE:
    case ADJUSTMENT:
      adjustedPrice = Number(adjustment);
      break;
    default:
      break;
  }
  return adjustedPrice;
};

/**
 * Function returns array of data objects for clients, who's id's is passed as selectedIds.
 * RTC (reasons to contact) service returns the reasons, split by categories.
 * Each of those categories has 'client' property.
 * @param {Array<{ keyIdentifier: string }>} categories
 * @param {Array<string>} selectedIds
 * @returns {Array<{ keyIdentifier: string }>} selectedClients
 */
export const getClientsByIdsFromRTCCategories = (categories, selectedIds) =>
  categories.flatMap((category) => {
    const { CMDId } = category.customer;

    return selectedIds.includes(category.customer.CMDId)
      ? [_omit({ profileId: CMDId, ...category.customer }, 'CMDId')]
      : [];
  });

/** Returns next nearest Saturday formatted ex: `February 12` */
export const getRTCExpirationDate = () => format(nextSaturday(Date.now()), 'MMMM d');

export const getNormalizedValue = (current, total) => {
  if (total === 0) {
    return 0;
  }
  return (current / total) * 100;
};

export const getMappedRTCCategories = (categories, data) => {
  const categoriesData = categories.map(({ name, numberOfClients: total }) => ({
    name,
    total,
    done: data.filter(({ completed, customerRTCType }) => {
      return (
        typeof customerRTCType === 'string' &&
        typeof name === 'string' &&
        customerRTCType.toLowerCase() === name.toLowerCase() &&
        completed
      );
    }).length,
  }));

  const { total, done } = categoriesData.reduce(
    (acc, { total, done }) => {
      acc.total += total;
      acc.done += done;
      return acc;
    },
    { total: 0, done: 0 },
  );

  const summaryData = { total, done };

  return {
    categoriesData,
    summaryData,
  };
};

/**
 * Function returns map of queryParams to be sent in customer service request
 * while fetching client's list of available filter facets.
 * @param {{ [x: string]: string }} params
 */
export const mapFilteredParams = (params, returnFacets = false) => {
  const faucetsFilters = ['departments', 'designers', 'marketingEvents', 'divisions'];
  const filteredParams = Object.keys(params)
    .filter((paramName) => (paramName !== 'searchLevel' && params[paramName]) || params[paramName] === 0)
    .reduce(
      (result, paramName) => {
        if (faucetsFilters.includes(paramName)) {
          const selectedParams = params[paramName].split(',').map(encodeURIComponent);
          if (!selectedParams.length) return result;

          const currentParam = `${paramName}:${selectedParams.join(';')}`;
          const applyFacets = result.applyFacets ? `${result.applyFacets},${currentParam}` : currentParam;
          return {
            ...result,
            applyFacets,
          };
        }

        return { ...result, [paramName]: params[paramName] };
      },
      { returnFacets },
    );

  if (filteredParams.shoppedFrom) {
    filteredParams.shoppedFrom = convertFilterValueToSeconds(filteredParams.shoppedFrom);
  }
  if (filteredParams.contactedFrom) {
    filteredParams.contactedFrom = convertFilterValueToSeconds(filteredParams.contactedFrom);
  }
  if (filteredParams.contactedTill) {
    filteredParams.contactedTill = convertFilterValueToSeconds(filteredParams.contactedTill);
  }

  return filteredParams;
};

export const fillEmptyLetters = (alphabetMap) => {
  const alphabetWithAllLetters = alphabet.reduce((acc, letter) => {
    if (alphabetMap[letter] !== undefined) {
      acc[letter] = alphabetMap[letter];
    } else {
      acc[letter] = null;
    }

    return acc;
  }, {});

  const isSpecialCharsInAlphabetMap = !Object.keys(alphabetMap)[0].match(/[A-Z]/);

  if (isSpecialCharsInAlphabetMap) {
    alphabetWithAllLetters['#'] = 0;
  } else {
    alphabetWithAllLetters['#'] = null;
  }

  return alphabetWithAllLetters;
};

export const getVisibleRTCItems = (categoryRTCItems, filters) => {
  const { actionTypes, badges, assets } = filters;
  let result = categoryRTCItems;

  if (actionTypes?.length) {
    result = result.filter(({ actionType }) => actionTypes.includes(actionType));
  }

  if (badges?.length) {
    result = result.filter((item) => !_intersection(badges, item.badges).length);
  }

  if (assets?.length) {
    result = result.filter((item) => _intersection(assets, item.assets).length);
  }

  return result;
};

export const shouldClearSortBy = (currentFilter, currentSort = '', newFilter) => {
  const sortedByPointBalance = currentSort.includes('pointBalance');
  const sortedByPromoBalance = currentSort.includes('promoBalance');
  const onlyOneFilterAsset = newFilter.withAssets && newFilter.withAssets.split(',').length === 1;
  const filterPromoAssetDifferentSorting = newFilter.withAssets === 'PROMO' && sortedByPointBalance;
  const filterPointAssetDifferentSorting = newFilter.withAssets === 'POINT' && sortedByPromoBalance;

  if (
    currentSort &&
    ((currentFilter.withAssets && !newFilter.withAssets && (sortedByPointBalance || sortedByPromoBalance)) ||
      (onlyOneFilterAsset && (filterPromoAssetDifferentSorting || filterPointAssetDifferentSorting)))
  ) {
    return true;
  }

  return false;
};

export const filterKiNumbersByPlccConversion = (kiNumbers = []) => kiNumbers.filter((item) => item.length === 16);

export const formatReminderIsoDate = (value) => {
  if (!value) return null;
  const editedValue = typeof value === 'string' ? value.replace(/-/g, '/') : value;
  switch (value) {
    case 'Tomorrow':
      return format(add(Date.now(), { days: 1 }), 'yyyy-MM-dd');
    case 'Next Week':
      return format(add(Date.now(), { weeks: 1 }), 'yyyy-MM-dd');
    case 'Next Month':
      return format(add(Date.now(), { months: 1 }), 'yyyy-MM-dd');
    default:
      return format(new Date(editedValue), 'yyyy-MM-dd');
  }
};

/**
 * Returns reminders to be shown on client account tab.
 * @param {{ reminders: Array<ReminderPropType> }} reminders
 * @returns {{ visibleReminders: Array<ReminderPropType> }}
 */

export const getClientAccountSectionReminders = (reminders) => {
  const visibleRemindersCount = 3;
  return _sortBy(reminders, [
    ({ completed }) => completed,
    ({ reminderIsoDate }) => (reminderIsoDate ? new Date(reminderIsoDate) : ''),
    ({ createdIsoDateTime }) => new Date(createdIsoDateTime),
  ]).slice(0, visibleRemindersCount);
};

export const mapProfileData = ({ isClientInformationFromUcaEnabled, ...payload }) => {
  // additionalEmail and additionalPhone are mapped according to requirements in
  // https://neimanmarcus.atlassian.net/browse/OSP-1757?focusedCommentId=774663&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-774663
  const additionalEmail = payload.emails[0];
  const profileData = convertStylePreferences(payload);

  let additionalPhone;
  let additionalPhoneType;

  if (payload.phones[0]) {
    additionalPhone = payload.phones[0].number;
    additionalPhoneType = payload.phones[0].type;
  }

  const userOnlineProfiles = isClientInformationFromUcaEnabled ? payload.ucaProfiles : payload.onlineProfiles;

  return {
    ...profileData,
    additionalPhone,
    additionalPhoneType,
    additionalEmail,
    onlineProfiles: userOnlineProfiles.map((profile) => {
      const { sourceName } = profile;

      return {
        ...profile,
        profileType: sourceName === 'NM' || sourceName === 'BG' ? `${sourceName}O` : sourceName,
      };
    }),
  };
};

export const mapAddress = (address = {}) => ({
  ...(address.firstName && { firstName: address.firstName }),
  ...(address.lastName && { lastName: address.lastName }),
  line1: address.line1 || address.addressLine1 || address.address1,
  line2: address.line2 || address.addressLine2 || address.address2,
  city: address.city,
  country: address.country || address.countryCode || 'US',
  state: address.state || address.stateCode,
  zip: address.zip || address.postalCode || address.zipCode || '00000',
  default: address.defaultAddress || address.default,
  phone: address.dayTelephone || address.phone,
  contactAddressName: address.contactAddressName,
  addressId: address.addressId,
  addressType: address.addressType,
});

export const mapClientData = (payload) => {
  const { emailAddress, authenticationStatus, ...data } = payload;
  return {
    ...data,
    email: payload.email || payload.emailAddress,
    payments: payload.payments
      .filter((payment) => !!payment?.maskedNumber)
      .map((payment) => {
        return {
          ...payment,
          billingAddress: mapAddress(payment.billingAddress),
        };
      }),
    billingAddress: payload.billingAddress?.map((address, index) => ({
      ...mapAddress(address),
      id: index.toString(),
    })),
    shippingAddress: payload.shippingAddress.map((address, index) => ({
      ...mapAddress(address),
      id: index.toString(),
    })),
  };
};

export const updateDefaultAddress = (arr = [], payload, isBillingAddress) => {
  if (
    (isBillingAddress && payload.addressType !== 'billing') ||
    (!isBillingAddress && payload.addressType === 'billing')
  ) {
    return arr;
  }

  const obj = { ...mapAddress(payload), id: arr.length.toString() };
  if (payload.defaultAddress) {
    const updatedArr = arr.map((item) => ({ ...item, default: false }));
    return [...updatedArr, obj];
  }
  return [...arr, obj];
};

export const convertStringAmountToNumber = (strAmt = '') => {
  if (typeof strAmt !== 'string') return 0;
  const result = parseFloat(strAmt.replace(/\$|,/g, ''));

  return Number.isNaN(result) ? 0 : result;
};

/**
 * For BG Brand, if the sumtotal of all regular items in Checkout is more than $700, gift wrap is Free,
 * excluding sale and adjusted items from the sumtotal. (Business asked to override
 * the gift wrapping fee and total in checkout, order receipt and order confirmation page)
 * For more details see: https://neimanmarcus.atlassian.net/browse/OSP-9297
 */
export const totalAmountForElevatedPackaging = (conciergeProducts = []) => {
  return conciergeProducts?.reduce((runningEligibleTotal, product) => {
    const {
      price: { adjustedPrice, currentPrice },
      onSale,
      quantity,
      promotionDiscAmt,
    } = product;
    const hasPromoDiscAmt = convertStringAmountToNumber(promotionDiscAmt) > 0;
    const hasAdjustedPrice = convertStringAmountToNumber(adjustedPrice) > 0;
    const totalItemPrice = currentPrice * quantity;
    let saleNotready = false;
    if (product?.hasOwnProperty('onSale')) {
      if (onSale === undefined) {
        saleNotready = true;
      }
      if (saleNotready || onSale) {
        return runningEligibleTotal;
      }
    }
    return hasPromoDiscAmt || hasAdjustedPrice ? runningEligibleTotal : runningEligibleTotal + totalItemPrice;
  }, 0);
};

export const mapOrderPricing = (orderInfo) => {
  const {
    subtotal,
    totalOrderAmount,
    totalStateTaxAmount = 0,
    totalCountyTaxAmount = 0,
    totalCityTaxAmount = 0,
    totalLocalTaxAmount = 0,
    promotion,
  } = orderInfo;
  const taxRate = (
    Number(totalStateTaxAmount) +
    Number(totalCountyTaxAmount) +
    Number(totalCityTaxAmount) +
    Number(totalLocalTaxAmount)
  ).toFixed(2);

  return {
    subTotalPrice: `$${subtotal}`,
    orderTotalTax: `$${taxRate}`,
    orderTotalAmt: `$${totalOrderAmount}`,
    promotion,
  };
};

export const mapVoidOrderPricing = (orderInfo, serviceItems) => {
  const giftWrapItem = serviceItems.find((item) => item.service === GIFT_WRAP_LINE_ITEM.departmentCode);
  const filterGCPayments = orderInfo.additionalPaymentDetails.length
    ? orderInfo.additionalPaymentDetails.filter((e) => e.type === LOYALTY_CARD_TYPES.NEX)
    : null;
  const totalCardDiscount = filterGCPayments
    ? +filterGCPayments.reduce((total, payment) => total + +(payment.amountReceived || 0), 0)
    : null;

  return {
    ...mapOrderPricing(orderInfo),
    ...(totalCardDiscount ? { giftCard: `$${totalCardDiscount.toFixed(2)}` } : {}),
    ...(giftWrapItem ? { giftPackaging: `$${giftWrapItem.price.toFixed(2)}` } : {}),
  };
};

/**
 * @param {Date string} created
 * @param {Date string} updated
 * @returns {string}
 */
export const getWishlistDisplayedDate = (created, updated) => {
  const createdTimestamp = new Date(created).getTime();
  const updatedTimestamp = new Date(updated).getTime();
  // If difference in updated and created timestamp ar more than this number then wishlist was updated
  const minimumDifference = 10000;

  if (updatedTimestamp - createdTimestamp > minimumDifference) {
    return `Last updated on: ${formatNoteDetailsDate(updated)}`;
  }

  return `Created on: ${formatNoteDetailsDate(created)}`;
};

/**
 * Sort Wishlist items, display first items with images
 * @param {Array} wishlistItems
 * @returns {Array}
 */
export const sortProductsForWishlist = (wishlistItems) => {
  return _orderBy(wishlistItems, ({ images }) => {
    const image = images && images.length && images[0].url;
    return !image || image.includes(NO_IMAGE_AVAILABLE.BG) || image.includes(NO_IMAGE_AVAILABLE.NM);
  });
};

/**
 * Save client id to local storage for further be able to add products to client wishlist
 * @param clientId
 */
export const saveUserForWishlist = (clientId) => {
  window.localStorage.setItem(CLIENT_WISHLIST_STORAGE_NAME, clientId);
};

/**
 * Map farfetch product data to format used for wishlist
 * @param productData
 * @returns {{brandName: (*|string), pimSkuId: string, images: [{url: (*|string), order: number}], productName: string, pimStyleId: string}}
 */
export const mapProductDataForWishlist = (productData) => {
  const { name, pimSkuId, productImages } = productData;

  // Currently wishlist only available for BG users
  const imageUrl = productImages && productImages.length ? productImages[0] : NO_IMAGE_AVAILABLE.BG;

  return {
    productName: name || '',
    brandName: productData?.designer?.name || '',
    pimSkuId: String(pimSkuId),
    pimStyleId: String(productData?.metadata?.pimStyle),
    images: [
      {
        order: 1,
        url: imageUrl,
      },
    ],
  };
};

export const getGroupedServicesByDept = (items = []) =>
  items.reduce((acc, item = {}, i) => {
    const { dept, price: { orderPrice = 0 } = {}, quantity = 0, serviceName, cmosSkuId } = item;

    if (dept && !acc[i]) {
      acc[i] = { service: dept, price: 0 };
      acc[i].title = ORDER_SERVICES_DEPT_TYPE[dept];
      acc[i].price += orderPrice * quantity;
      acc[i].serviceName = serviceName;
      acc[i].sku = cmosSkuId;
    }

    return acc;
  }, {});

export const mapPhonesToContactsList = (phones, primaryPhoneNumber) => {
  return phones.map((phone) => ({
    type: phone.type,
    value: phone.number,
    isPrimary: phone.number === primaryPhoneNumber,
  }));
};

export const mapEmailsToContactsList = (emails, primaryEmail) => {
  return emails.map((email) => ({
    type: null,
    value: email,
    isPrimary: email === primaryEmail,
  }));
};

export const redirectToCCDetailsPage = (history, { associatePin, timestamp }) => {
  return history.push(`/clients/reason-to-contact-details?associatePin=${associatePin}&timestamp=${timestamp}`);
};

export const convert12hTo24h = (time) => {
  //TO-DO: update it once the BE service is implemented
  if (!time) return 0;

  const modifier = time.slice(-2);
  let hours = parseInt(time, 10);
  if (hours === 12) {
    hours = 0;
  }
  if (modifier === 'PM') {
    hours += 12;
  }
  return hours;
};

export const validateStoreHours = (storeInfo, localTime, currentDay) => {
  // weekdays numbered from 1 to 7 in ISO
  const currentDayISO = currentDay || getCurrentWeekDayISO(storeInfo.timeZone);
  const todaysWorkHour = storeInfo.workingHours[Number(currentDayISO) - 1].hours[0];
  const startEndHours = todaysWorkHour?.split(' - ') || [];
  const hours = startEndHours.length === 2 ? startEndHours : [0, 0];
  const currentLocalTimeHHMM = localTime || getCurrentLocalTimeHHMM(storeInfo.timeZone);

  return getEachMinuteOfInterval(convert12hTo24h(hours[0]), convert12hTo24h(hours[1])).includes(currentLocalTimeHHMM);
};
